<template>
    <vx-box class="@container p-4 flex flex-col gap-2 h-full">
        <div class="flex flex-col min-w-0 @[220px]:flex-row @[220px]:justify-between">
            <div>
                <div class="flex items-center gap-1.5">
                    <div class="size-2 rounded-[2px]" :style="{ backgroundColor: '#' + category.color }"></div>
                    <span class="text-xs">{{ category.name }}</span>
                </div>
                <p class="text-sm font-semibold text-slate-800 truncate">
                    {{ props.thread.contact_name || formatPhoneNumber(props.thread.contact_phone ?? '') || 'Unknown' }}
                </p>
            </div>

            <div>
                <p class="text-xs text-slate-400">{{ timeAgo }}</p>
            </div>
        </div>

        <p class="text-sm text-slate-600 line-clamp-3 flex-1">
            {{ props.thread.last_message_content ?? '' }}
        </p>

        <motion.div
            v-if="canShowSuggestion"
            :animate="{
                height: suggestion === undefined ? 34 : suggestion ? 'auto' : 0,
                opacity: suggestion === '' ? 0 : 1,
            }"
            :transition="{ duration: 0.3 }"
            class="overflow-hidden"
        >
            <div v-if="suggestion === undefined" class="rounded-lg bg-slate-300 animate-pulse h-[34px]"></div>
            <div v-else-if="suggestion" class="rounded-lg bg-violet-50 border border-violet-100 p-3">
                <div class="flex items-center gap-1.5 mb-1">
                    <font-awesome-icon :icon="faSparkles" class="text-violet-500 text-xs"></font-awesome-icon>
                    <span class="text-xs font-semibold text-violet-600">AI Suggested Reply</span>
                </div>
                <p class="text-sm text-violet-900 line-clamp-2">
                    {{ suggestion }}
                </p>
            </div>
        </motion.div>

        <div class="grid grid-cols-1 items-center gap-2 pt-1" :class="{ '@[220px]:grid-cols-2': canSnoozeThread }">
            <snooze-thread-dropdown
                v-if="canSnoozeThread"
                :thread="props.thread"
                placement="right"
                teleport
                @snoozed="emit('snoozed')"
                @unsnoozed="emit('unsnoozed')"
                v-slot:default="{ snoozing, snoozed }"
            >
                <vx-button color="muted" block size="md" :loading="snoozing">
                    {{ snoozed ? 'Unsnooze' : 'Snooze' }}
                    <font-awesome-icon
                        class="hidden! @[220px]:inline-block!"
                        widthAuto
                        :icon="snoozed ? faClockRotateLeft : faClock"
                    ></font-awesome-icon>
                </vx-button>
            </snooze-thread-dropdown>
            <router-button
                :to="{
                    name: 'message-hub.conversation',
                    params: { contactId: props.thread.contact_id },
                    query: {
                        ...route.query,
                        group: currentOrgUnit && props.thread.group_id ? props.thread.group_id : undefined,
                    },
                }"
                color="primary"
                size="md"
            >
                Reply
                <font-awesome-icon class="hidden! @[220px]:inline-block!" widthAuto :icon="faReply"></font-awesome-icon>
            </router-button>
        </div>
    </vx-box>
</template>

<script lang="ts" setup>
import { faClock, faClockRotateLeft, faReply, faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxButton } from '@voxie/frontend-components';
import { computed, inject, ref, watch } from 'vue';
import { motion } from 'motion-v';
import { useIntelligentInbox } from '../useIntelligentInbox';
import SnoozeThreadDropdown from '~/components/message-hub/intelligent-inbox/SnoozeThreadDropdown.vue';
import { canUseIntelligentInbox, shouldShowThreadSuggestion } from '~/components/message-hub/messageHubUtil';
import type { Features } from '~/provides/features.types';
import type { Thread } from '~/services/threads/threadsClient.types';
import { formatPhoneNumber } from '~/components/filters';
import dayjs from '~/utils/dayjs';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useRoute } from 'vue-router';
import RouterButton from '~/components/general/RouterButton.vue';

const props = defineProps<{
    thread: Thread;
}>();

const emit = defineEmits<{
    snoozed: [];
    unsnoozed: [];
}>();

const FEATURES = inject('FEATURES') as Features;

const route = useRoute();
const { currentOrgUnit } = useCurrentGroup();
const { categories, fetchSuggestion } = useIntelligentInbox();
const suggestion = ref<string | undefined>(undefined);

const canShowSuggestion = computed(() => {
    return shouldShowThreadSuggestion(FEATURES, props.thread);
});

const suggestionTarget = computed(() => {
    if (!canShowSuggestion.value) {
        return undefined;
    }

    return {
        contactId: props.thread.contact_id,
        groupId: props.thread.group_id ?? '',
    };
});

const timeAgo = computed(() => {
    return props.thread.last_message_at ? dayjs.utc(props.thread.last_message_at).fromNow() : '';
});

const category = computed<{ name: string; color: string }>(() => {
    if (props.thread.thread_category_id !== null && props.thread.thread_category_id !== undefined) {
        const found = categories.value?.find((c) => c.id === props.thread.thread_category_id);
        if (found) {
            return found;
        }
    }
    return { name: 'Uncategorized', color: '94a3b8' };
});

const canSnoozeThread = computed(() => canUseIntelligentInbox(FEATURES));

watch(
    suggestionTarget,
    async (target, _previousTarget, onCleanup) => {
        if (!target) {
            suggestion.value = '';
            return;
        }

        suggestion.value = undefined;

        let cancelled = false;
        onCleanup(() => {
            cancelled = true;
        });

        const reply = await fetchSuggestion(target.groupId, target.contactId);
        if (!cancelled) {
            suggestion.value = reply;
        }
    },
    { immediate: true }
);
</script>
